import {
	getPeriodDateType,
	getPeriodType,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/period-dropdown-options.tsx';
import { getDayCountConventionTranslation } from '@dealstack/frontend/shared/incomeRights.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import {
	DataSecurityWithAllFeaturesAndRanksTct,
	SecurityCatchUpRightsBasis,
	SecurityExchangeRightsPeriodBasis,
	SecurityFeatureType,
	SecurityInterestDefinitionPrincipal,
	SecurityIssuedIn,
	RatchetMetricType,
	SecurityLiquidationPreferenceRes,
} from '@dealstack/shared/lib/generated';
import { Flex, Text } from '@mantine/core';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { formatPercentage } from '@dealstack/frontend/app/pages/Valuations/components/table/utils/formatting.ts';

const makeInterestContent = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>,
	security: DataSecurityWithAllFeaturesAndRanksTct
): JSX.Element => {
	const interestDefinition = security.securityInterestDefinition;
	let principalDescription = '';
	let compoundingDescription = '';
	let startDescription = '';

	// Compute principalDescription
	if (security.issuedIn === SecurityIssuedIn.CURRENCY) {
		principalDescription = tCapManage(
			'securityFinancialTermsDrawer.principalDescription.currency'
		);
	} else if (security.issuedIn === SecurityIssuedIn.UNITS) {
		if (
			interestDefinition?.principal ===
			SecurityInterestDefinitionPrincipal.ISSUE_COST_PER_UNIT
		) {
			principalDescription = tCapManage(
				'securityFinancialTermsDrawer.principalDescription.issueCostPerUnit'
			);
		} else if (
			interestDefinition?.principal === SecurityInterestDefinitionPrincipal.VALUE_PER_UNIT
		) {
			principalDescription = tCapManage(
				'securityFinancialTermsDrawer.principalDescription.valuePerUnit',
				{ deemedValuePerUnit: interestDefinition?.principalValue }
			);
		} else if (
			interestDefinition?.principal ===
			SecurityInterestDefinitionPrincipal.VALUE_FOR_TOTAL_ISSUE
		) {
			principalDescription = tCapManage(
				'securityFinancialTermsDrawer.principalDescription.valueForTotalIssue',
				{ totalDeemedValue: interestDefinition?.principalValue }
			);
		}
	}

	// Compute compoundingDescription
	if (interestDefinition?.compoundingPeriod) {
		if (interestDefinition?.firstInterestDate) {
			compoundingDescription = tCapManage(
				'securityFinancialTermsDrawer.compoundingDescription.firstInterestDate',
				{
					compoundingPeriod: getPeriodType(
						tCommonUi,
						interestDefinition.compoundingPeriod
					),
					firstInterestDate: formatStringDateToUI(interestDefinition.firstInterestDate),
				}
			);
		} else {
			compoundingDescription = tCapManage(
				'securityFinancialTermsDrawer.compoundingDescription.noFirstInterestDate',
				{
					compoundingPeriod: getPeriodType(
						tCommonUi,
						interestDefinition.compoundingPeriod
					),
				}
			);
		}
	}

	if (interestDefinition?.deemedStartDate) {
		startDescription = tCapManage(
			'securityFinancialTermsDrawer.startDescription.deemedStartDate',
			{
				deemedStartDate: formatStringDateToUI(interestDefinition.deemedStartDate),
			}
		);
	} else {
		startDescription = tCapManage('securityFinancialTermsDrawer.startDescription.noStartDate');
	}

	return (
		<Text c="dark.0">
			{tCapManage('securityFinancialTermsDrawer.interestContent.output', {
				securityName: security.name,
				interestFloatingRate: interestDefinition?.interestFloatingRate || 0,
				principalDescription,
				accrualPeriod: getPeriodType(tCommonUi, interestDefinition?.accrualPeriod) || '',
				dayCountConvention:
					getDayCountConventionTranslation(
						tCommonUi,
						interestDefinition?.dayCountConvention
					) || '',
				compoundingDescription,
				startDescription,
			})}
		</Text>
	);
};

const makePrincipalContent = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	security: DataSecurityWithAllFeaturesAndRanksTct
): JSX.Element => {
	const interestDefinition = security.securityInterestDefinition;
	let principalDescription = '';

	if (security.issuedIn === SecurityIssuedIn.CURRENCY) {
		principalDescription = tCapManage(
			'securityFinancialTermsDrawer.principalContent.currency',
			{
				securityName: security.name,
			}
		);
	} else if (security.issuedIn === SecurityIssuedIn.UNITS) {
		switch (interestDefinition?.principal) {
			case SecurityInterestDefinitionPrincipal.ISSUE_COST_PER_UNIT:
				principalDescription = tCapManage(
					'securityFinancialTermsDrawer.principalContent.issueCostPerUnit',
					{
						securityName: security.name,
					}
				);
				break;
			case SecurityInterestDefinitionPrincipal.VALUE_PER_UNIT:
				principalDescription = tCapManage(
					'securityFinancialTermsDrawer.principalContent.valuePerUnit',
					{
						securityName: security.name,
						deemedValuePerUnit: interestDefinition?.principalValue || 0,
					}
				);
				break;
			case SecurityInterestDefinitionPrincipal.VALUE_FOR_TOTAL_ISSUE:
				principalDescription = tCapManage(
					'securityFinancialTermsDrawer.principalContent.valueForTotalIssue',
					{
						securityName: security.name,
						totalDeemedValue: interestDefinition?.principalValue || 0,
					}
				);
				break;
			default:
				break;
		}
	}

	return <Text c="dark.0">{principalDescription}</Text>;
};

const makeCatchUpContent = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	security: DataSecurityWithAllFeaturesAndRanksTct
): JSX.Element | null => {
	const catchUpRights = security.securityCatchUpRights;

	if (!catchUpRights) {
		return null;
	}

	let text = '';

	if (
		catchUpRights.basis ===
		SecurityCatchUpRightsBasis.DISTRIBUTIONS_TO_SECURITIES_IN_HIGHER_RANK
	) {
		text = tCapManage('securityFinancialTermsDrawer.catchUpContent.higherRank', {
			securityName: security.name,
			target: catchUpRights.target,
		});
	} else {
		text = tCapManage('securityFinancialTermsDrawer.catchUpContent.higherOrEqual', {
			securityName: security.name,
			target: catchUpRights.target,
		});
	}

	return <Text c="dark.0">{text}</Text>;
};

const makeTrackingContent = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	security: DataSecurityWithAllFeaturesAndRanksTct,
	selectedEntityName: string
): JSX.Element | null => {
	const tracking = security.tracking;

	if (!tracking || tracking.length === 0) {
		return null;
	}

	const trackedNames = tracking.map((t) => `${t.securityName} (${t.currency})`);
	let securities = '';
	let trackedSecurityNames = '';

	if (trackedNames.length === 1) {
		securities = tCapManage('securityFinancialTermsDrawer.trackingContent.securities.single', {
			securityName: security.name,
			trackedSecurityName: trackedNames[0],
		});
		trackedSecurityNames = trackedNames[0];
	} else {
		const lastTrackedName = trackedNames[trackedNames.length - 1];
		const remainingTrackedNames = trackedNames.slice(0, -1).join(', ');
		trackedSecurityNames = tCapManage(
			'securityFinancialTermsDrawer.trackingContent.securities.list',
			{
				remainingTrackedNames,
				lastTrackedName,
			}
		);

		securities = tCapManage(
			'securityFinancialTermsDrawer.trackingContent.securities.multiple',
			{
				securityName: security.name,
				trackedSecurityNames,
			}
		);
	}

	return (
		<>
			<Text c="dark.0" mb="1rem">
				{securities}
			</Text>

			<Text c="dark.0">
				{tCapManage('securityFinancialTermsDrawer.trackingContent.holders', {
					securityName: security.name,
					issuerName: selectedEntityName,
					securitiesLabel: trackedSecurityNames,
				})}
			</Text>
		</>
	);
};

const makeResidueContent = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	security: DataSecurityWithAllFeaturesAndRanksTct,
	selectedEntityName: string
): JSX.Element => {
	const ratchetSchedule = security?.securityRatchetSchedule;

	const individualRatchetLabels = ratchetSchedule?.entries
		?.sort((entry) => entry.order)
		?.map((entry) => {
			const metricLabel =
				ratchetSchedule?.metricType === RatchetMetricType.XIRR
					? tCapManage('securityFinancialTermsDrawer.metric_label.xirr')
					: tCapManage('securityFinancialTermsDrawer.metric_label.moic');
			const threshold =
				ratchetSchedule?.metricType === RatchetMetricType.XIRR
					? formatPercentage(entry.xirrThreshold ?? 0)
					: `${entry.moicThreshold ?? 0}x`;
			const subjects =
				ratchetSchedule?.metricSubjects?.length === 1
					? tCapManage('securityFinancialTermsDrawer.subjects.single', {
							subject: ratchetSchedule.metricSubjects[0].personName,
						})
					: tCapManage('securityFinancialTermsDrawer.subjects.multiple', {
							subjects: ratchetSchedule.metricSubjects
								.map((subject) => subject.personName)
								.join(', '),
						});

			if (ratchetSchedule?.metricType === RatchetMetricType.MOIC_AND_XIRR) {
				return {
					id: entry.id,
					label: tCapManage('securityFinancialTermsDrawer.ratchet_label_moic_and_xirr', {
						moicThreshold: `${entry.moicThreshold ?? 0}x`,
						moicLabel: tCapManage('securityFinancialTermsDrawer.metric_label.moic'),
						xirrThreshold: formatPercentage(entry.xirrThreshold ?? 0),
						xirrLabel: tCapManage('securityFinancialTermsDrawer.metric_label.xirr'),
						subjects,
						entitlement: formatPercentage(entry.entitlementPercentage),
					}),
				};
			}

			return {
				id: entry.id,
				label: tCapManage('securityFinancialTermsDrawer.ratchet_label', {
					threshold,
					metricLabel,
					subjects,
					entitlement: formatPercentage(entry.entitlementPercentage),
				}),
			};
		});

	return (
		<>
			<Text c="dark.0">
				{tCapManage('securityFinancialTermsDrawer.residueContent', {
					securityName: security.name,
					issuerName: selectedEntityName,
				})}
			</Text>

			{ratchetSchedule && (
				<>
					<Text fw={600} c="dark.9" mt="2rem" mb="0.5rem">
						{tCapManage('securityFinancialTermsDrawer.title.ratchets', {
							securityName: security.name,
							initialEntitlement: formatPercentage(
								ratchetSchedule.initialEntitlementPercentage
							),
						})}
					</Text>

					{individualRatchetLabels?.map(({ id, label }) => (
						<Text key={id} c="dark.0" mt="0.5rem">
							{label}
						</Text>
					))}

					<Text c="dark.0" mt="0.5rem">
						{ratchetSchedule.distributeUntilBreakpoint
							? tCapManage(
									'securityFinancialTermsDrawer.ratchet_distribution.until_breakpoint'
								)
							: tCapManage(
									'securityFinancialTermsDrawer.ratchet_distribution.whole_step'
								)}
					</Text>
				</>
			)}
		</>
	);
};

const makeLiquidationPreferenceContent = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	security: DataSecurityWithAllFeaturesAndRanksTct,
	liquidationPreference: SecurityLiquidationPreferenceRes | null
): JSX.Element | null => {
	if (!liquidationPreference) {
		return null;
	}

	let description = '';

	switch (liquidationPreference.principal) {
		case SecurityInterestDefinitionPrincipal.ISSUE_COST_PER_UNIT:
			description = tCapManage(
				'securityFinancialTermsDrawer.liquidationPreferenceContent.issueCostPerUnit',
				{
					defaultValue:
						'Holders of {{securityName}} are entitled to a liquidation preference equal to the amount paid per unit at issue, paid out of the residue ahead of other residue holders.',
					securityName: security.name,
				}
			);
			break;
		case SecurityInterestDefinitionPrincipal.VALUE_PER_UNIT:
			description = tCapManage(
				'securityFinancialTermsDrawer.liquidationPreferenceContent.valuePerUnit',
				{
					defaultValue:
						'Holders of {{securityName}} are entitled to a liquidation preference of {{deemedValuePerUnit}} per unit, paid out of the residue ahead of other residue holders.',
					securityName: security.name,
					deemedValuePerUnit: liquidationPreference.principalValue ?? 0,
				}
			);
			break;
		case SecurityInterestDefinitionPrincipal.VALUE_FOR_TOTAL_ISSUE:
			description = tCapManage(
				'securityFinancialTermsDrawer.liquidationPreferenceContent.valueForTotalIssue',
				{
					defaultValue:
						'Holders of {{securityName}} are entitled to a liquidation preference of {{totalDeemedValue}} across the total issue, paid out of the residue ahead of other residue holders.',
					securityName: security.name,
					totalDeemedValue: liquidationPreference.principalValue ?? 0,
				}
			);
			break;
		default:
			return null;
	}

	return <Text c="dark.0">{description}</Text>;
};

const makeExchangeRightsContent = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>,
	security: DataSecurityWithAllFeaturesAndRanksTct,
	featureType: SecurityFeatureType.OPTION | 'WARRANT',
	selectedEntityName: string
): JSX.Element => {
	const exchangeRights = security.securityExchangeRights;

	// Compute sentence 1
	let sentence1 = '';

	if (featureType === 'WARRANT') {
		sentence1 = tCapManage(
			'securityFinancialTermsDrawer.exchangeRightsContent.sentence1.warrant',
			{
				securityName: security.name,
				exchangeRate: exchangeRights?.rate || 0,
				exchangedFor: exchangeRights?.exchangedForSecurity?.name || '',
			}
		);
	} else {
		sentence1 = tCapManage(
			'securityFinancialTermsDrawer.exchangeRightsContent.sentence1.option',
			{
				securityName: security.name,
				exchangeRate: exchangeRights?.rate || 0,
				exchangedFor: exchangeRights?.exchangedForSecurity?.name || '',
			}
		);
	}

	// Compute sentence 2
	let sentence2 = '';

	if (exchangeRights?.additionalPriceRate) {
		let compoundingDescription = '';
		let startDescription = '';

		if (exchangeRights?.additionalPriceCompoundingPeriod) {
			if (exchangeRights?.additionalPriceFirstInterestDate) {
				compoundingDescription = tCapManage(
					'securityFinancialTermsDrawer.exchangeRightsContent.sentence2.additionalPriceCompoundingPeriod',
					{
						compoundingPeriod: getPeriodType(
							tCommonUi,
							exchangeRights.additionalPriceCompoundingPeriod
						),
						firstDate: formatStringDateToUI(
							exchangeRights.additionalPriceFirstInterestDate
						),
					}
				);
			} else {
				compoundingDescription = tCapManage(
					'securityFinancialTermsDrawer.exchangeRightsContent.sentence2.additionalPriceNoFirstInterestDate',
					{
						compoundingPeriod: getPeriodType(
							tCommonUi,
							exchangeRights.additionalPriceCompoundingPeriod
						),
					}
				);
			}
		}

		if (exchangeRights?.additionalPriceDeemedStartDate) {
			startDescription = tCapManage(
				'securityFinancialTermsDrawer.exchangeRightsContent.sentence2.additionalPriceDeemedStartDate',
				{
					startDate: formatStringDateToUI(exchangeRights.additionalPriceDeemedStartDate),
				}
			);
		} else {
			startDescription = tCapManage(
				'securityFinancialTermsDrawer.exchangeRightsContent.sentence2.additionalPriceNoDeemedStartDate'
			);
		}

		sentence2 = tCapManage('securityFinancialTermsDrawer.exchangeRightsContent.sentence2Full', {
			issuerName: selectedEntityName,
			price: exchangeRights?.price || '',
			additionalPriceRate: exchangeRights?.additionalPriceRate || '',
			accrualPeriod:
				getPeriodType(tCommonUi, exchangeRights?.additionalPriceAccrualPeriod) || '',
			dayCountConvention:
				getDayCountConventionTranslation(
					tCommonUi,
					exchangeRights?.additionalPriceDayCountConvention
				) || '',
			compoundingDescription,
			startDescription,
		});
	} else {
		sentence2 = tCapManage(
			'securityFinancialTermsDrawer.exchangeRightsContent.sentence2.noAdditionalPriceRate',
			{
				issuerName: selectedEntityName,
				price: exchangeRights?.price || 0,
			}
		);
	}

	// Compute sentence 3
	let sentence3 = '';

	if (exchangeRights?.periodBasis === SecurityExchangeRightsPeriodBasis.RELATIVE_TO_ISSUE_DATE) {
		if (
			exchangeRights?.periodBasisIntervalStartDuration &&
			exchangeRights?.periodBasisIntervalEndDuration
		) {
			const start = `${exchangeRights.periodBasisIntervalStartDuration} ${getPeriodDateType(tCommonUi, exchangeRights.periodBasisIntervalStartPeriod)}`;
			const end = `${exchangeRights.periodBasisIntervalEndDuration} ${getPeriodDateType(tCommonUi, exchangeRights.periodBasisIntervalEndPeriod)}`;

			sentence3 = tCapManage(
				'securityFinancialTermsDrawer.exchangeRightsContent.sentence3.relativeToIssueDate.bothIntervals',
				{
					start,
					end,
				}
			);
		} else if (exchangeRights?.periodBasisIntervalEndDuration) {
			const end = `${exchangeRights.periodBasisIntervalEndDuration} ${getPeriodDateType(tCommonUi, exchangeRights.periodBasisIntervalEndPeriod)}`;

			sentence3 = tCapManage(
				'securityFinancialTermsDrawer.exchangeRightsContent.sentence3.relativeToIssueDate.endOnly',
				{
					end,
				}
			);
		}
	} else if (exchangeRights?.periodBasis === SecurityExchangeRightsPeriodBasis.FIXED_DATE) {
		const relativeEndDate = `${exchangeRights.periodBasisIntervalEndDuration} ${getPeriodDateType(tCommonUi, exchangeRights.periodBasisIntervalEndPeriod)}`;

		sentence3 = tCapManage(
			'securityFinancialTermsDrawer.exchangeRightsContent.sentence3.fixedDateText',
			{
				fixedDate: formatStringDateToUI(exchangeRights?.periodBasisDate),
				relativeEndDate,
			}
		);
	}

	return (
		<>
			<Text c="dark.0" mb="1rem">
				{sentence1}
			</Text>
			<Text c="dark.0" mb="1rem">
				{sentence2}
			</Text>
			<Text c="dark.0">{sentence3}</Text>
		</>
	);
};

export const getDrawerTitle = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	securityName: string,
	featureType: SecurityFeatureType | 'WARRANT'
): JSX.Element | null => {
	const onPart = `${tCapManage('securityFinancialTermsDrawer.title.on')} ${securityName}`;
	const titleMap: Partial<Record<SecurityFeatureType | 'WARRANT', string>> = {
		[SecurityFeatureType.INTEREST]: tCapManage('securityFinancialTermsDrawer.title.interest'),
		[SecurityFeatureType.PRINCIPAL]: tCapManage('securityFinancialTermsDrawer.title.principal'),
		[SecurityFeatureType.TRACKING]: tCapManage('securityFinancialTermsDrawer.title.tracking'),
		[SecurityFeatureType.CATCH_UP]: tCapManage('securityFinancialTermsDrawer.title.catch_up'),
		[SecurityFeatureType.OPTION]: tCapManage('securityFinancialTermsDrawer.title.option'),
		[SecurityFeatureType.RESIDUE]: tCapManage('securityFinancialTermsDrawer.title.residue'),
		[SecurityFeatureType.LIQUIDATION_PREFERENCE]: tCapManage(
			'securityFinancialTermsDrawer.title.liquidation_preference',
			{ defaultValue: 'Liquidation preference' }
		),
		WARRANT: tCapManage('securityFinancialTermsDrawer.title.warrant'),
	};

	const title = titleMap[featureType];

	if (!title) {
		return null;
	}

	return (
		<Text fw={600} c="dark.9" mb="0.5rem">
			{title} {onPart}
		</Text>
	);
};

export const getDrawerBody = (params: {
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>;
	tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>;
	security: DataSecurityWithAllFeaturesAndRanksTct;
	featureType: SecurityFeatureType | 'WARRANT';
	selectedEntityName: string;
	isMergedIncomeRight: boolean;
	liquidationPreference?: SecurityLiquidationPreferenceRes | null;
}): JSX.Element | null => {
	const {
		tCapManage,
		tCommonUi,
		security,
		featureType,
		selectedEntityName,
		isMergedIncomeRight,
		liquidationPreference = null,
	} = params;

	if (isMergedIncomeRight) {
		const interestElement = (
			<div>
				{getDrawerTitle(tCapManage, security.name, SecurityFeatureType.INTEREST)}
				{makeInterestContent(tCapManage, tCommonUi, security)}
			</div>
		);
		const principalElement = (
			<div>
				{getDrawerTitle(tCapManage, security.name, SecurityFeatureType.PRINCIPAL)}
				{makePrincipalContent(tCapManage, security)}
			</div>
		);

		return (
			<Flex direction="column" gap="2rem">
				{interestElement}
				{principalElement}
			</Flex>
		);
	}

	const title = getDrawerTitle(tCapManage, security.name, featureType);
	let content: JSX.Element | null = null;

	switch (featureType) {
		case SecurityFeatureType.INTEREST:
			content = makeInterestContent(tCapManage, tCommonUi, security);
			break;
		case SecurityFeatureType.PRINCIPAL:
			content = makePrincipalContent(tCapManage, security);
			break;
		case SecurityFeatureType.TRACKING:
			content = makeTrackingContent(tCapManage, security, selectedEntityName);
			break;
		case SecurityFeatureType.CATCH_UP:
			content = makeCatchUpContent(tCapManage, security);
			break;
		case SecurityFeatureType.RESIDUE:
			content = makeResidueContent(tCapManage, security, selectedEntityName);
			break;
		case SecurityFeatureType.LIQUIDATION_PREFERENCE:
			content = makeLiquidationPreferenceContent(tCapManage, security, liquidationPreference);
			break;
		case SecurityFeatureType.OPTION:
			content = makeExchangeRightsContent(
				tCapManage,
				tCommonUi,
				security,
				SecurityFeatureType.OPTION,
				selectedEntityName
			);
			break;
		case 'WARRANT':
			content = makeExchangeRightsContent(
				tCapManage,
				tCommonUi,
				security,
				'WARRANT',
				selectedEntityName
			);
			break;
		default:
			return null;
	}

	return (
		<>
			{title}
			{content}
		</>
	);
};
